/**
 * Flight Data Monitor
 * Tracks flight data receipt for staleness detection and blank state classification
 * This module acts as a bridge between React contexts and the heartbeat service
 */

// Tracking state
let lastFlightUpdateTimestamp = null;
let flightsReceivedCount = 0;      // Flights received from API (before filtering)
let flightsDisplayedCount = 0;     // Flights actually displayed (after filtering)
let socketConnected = false;
let domDataRowCount = 0;           // Actual DOM rows with flight data
let lastHealthyRenderTimestamp = null;

// Blank state classification
export const BlankReason = {
  NONE: 'NONE',                    // Not blank - healthy
  EMPTY: 'EMPTY',                  // No flights from API (legitimate empty schedule)
  FILTERED_OUT: 'FILTERED_OUT',    // API returned flights but all filtered out
  BROKEN_UI: 'BROKEN_UI',          // Data exists but DOM shows no rows
  NETWORK_STALE: 'NETWORK_STALE',  // Data too old (network issues)
  WEBVIEW_FREEZE: 'WEBVIEW_FREEZE' // Multiple recovery attempts failed
};

// Thresholds
const STALE_THRESHOLD_MS = 60 * 1000;  // 60 seconds for network stale

/**
 * Update the last flight update timestamp
 * Called from Arrival/Departure contexts when flight data is received
 */
export function recordFlightDataReceipt(timestamp = new Date()) {
  lastFlightUpdateTimestamp = timestamp;
  console.log('[FlightDataMonitor] Flight data received at:', timestamp?.toISOString());
}

/**
 * Update the number of flights received from API (before filtering)
 * Called from Arrival/Departure contexts when raw data is received
 */
export function updateFlightsReceivedCount(count) {
  flightsReceivedCount = count;
}

/**
 * Update the number of flights currently displayed (after filtering)
 * Called from Arrival/Departure components after filtering
 */
export function updateFlightsDisplayedCount(count) {
  flightsDisplayedCount = count;
  // If we have displayed flights, record healthy render
  if (count > 0) {
    lastHealthyRenderTimestamp = Date.now();
  }
}

/**
 * Update the actual DOM data row count
 * Called from self-healing hook after DOM inspection
 */
export function updateDomDataRowCount(count) {
  domDataRowCount = count;
}

/**
 * Update Socket.IO connection status
 * Called from Arrival/Departure contexts on socket connect/disconnect
 */
export function updateSocketStatus(connected) {
  socketConnected = connected;
  console.log('[FlightDataMonitor] Socket.IO connection status:', connected);
}

/**
 * Get the last healthy render timestamp
 */
export function getLastHealthyRenderTimestamp() {
  return lastHealthyRenderTimestamp;
}

/**
 * Classify the current blank state
 * Returns a BlankReason enum value
 *
 * Priority order (most likely causes first):
 * 1. NETWORK_STALE - data is too old (network issue)
 * 2. EMPTY - no flights from API (legitimate empty schedule)
 * 3. FILTERED_OUT - API returned flights but all filtered out
 * 4. BROKEN_UI - data exists but DOM shows no rows
 * 5. WEBVIEW_FREEZE - multiple recovery attempts failed with fresh data
 */
export function classifyBlankState(recoveryAttempts = 0) {
  const now = Date.now();
  const lastUpdate = lastFlightUpdateTimestamp ? new Date(lastFlightUpdateTimestamp).getTime() : null;
  const timeSinceUpdate = lastUpdate ? now - lastUpdate : null;
  const isStale = !lastUpdate || timeSinceUpdate > STALE_THRESHOLD_MS;

  // PRIORITY 1: Check for network staleness first (most common cause)
  // This should NOT be overridden by WEBVIEW_FREEZE
  if (isStale) {
    return BlankReason.NETWORK_STALE;
  }

  // PRIORITY 2: No flights received from API (legitimate empty schedule)
  if (flightsReceivedCount === 0) {
    return BlankReason.EMPTY;
  }

  // PRIORITY 3: Flights received but none displayed (filtered out)
  if (flightsReceivedCount > 0 && flightsDisplayedCount === 0) {
    return BlankReason.FILTERED_OUT;
  }

  // PRIORITY 4: Flights should be displayed but DOM is empty (broken UI)
  // Only classify as BROKEN_UI initially; escalate to WEBVIEW_FREEZE after retries
  if (flightsDisplayedCount > 0 && domDataRowCount === 0) {
    // PRIORITY 5: WEBVIEW_FREEZE only if we have fresh data but multiple
    // recovery attempts have failed - indicates a true WebView/rendering issue
    if (recoveryAttempts >= 3) {
      return BlankReason.WEBVIEW_FREEZE;
    }
    return BlankReason.BROKEN_UI;
  }

  return BlankReason.NONE;
}

/**
 * Get flight data staleness status
 * Returns metadata for heartbeat payload
 */
export function getFlightDataStatus() {
  const now = Date.now();
  const lastUpdate = lastFlightUpdateTimestamp ? new Date(lastFlightUpdateTimestamp).getTime() : null;
  const timeSinceUpdate = lastUpdate ? now - lastUpdate : null;

  // Consider data stale if:
  // - Never received data, OR
  // - No update in last 5 minutes (for heartbeat purposes)
  const isStale = !lastUpdate || timeSinceUpdate > 5 * 60 * 1000;

  const status = {
    lastFlightUpdate: lastFlightUpdateTimestamp?.toISOString() || null,
    flightDataStale: isStale,
    socketConnected: socketConnected,
    flightsReceived: flightsReceivedCount,
    flightsDisplayed: flightsDisplayedCount,
    domDataRowCount: domDataRowCount,
    timeSinceUpdateSeconds: timeSinceUpdate ? Math.floor(timeSinceUpdate / 1000) : null
  };

  if (isStale && lastUpdate) {
    console.warn(`⚠️ [FlightDataMonitor] Flight data is STALE (${Math.floor(timeSinceUpdate / 1000)}s since last update)`);
  }

  return status;
}

/**
 * Get comprehensive status for self-healing decisions
 */
export function getComprehensiveStatus(recoveryAttempts = 0) {
  const status = getFlightDataStatus();
  const blankReason = classifyBlankState(recoveryAttempts);

  return {
    ...status,
    blankReason,
    isBlank: blankReason !== BlankReason.NONE,
    lastHealthyRenderAt: lastHealthyRenderTimestamp ? new Date(lastHealthyRenderTimestamp).toISOString() : null
  };
}

/**
 * Reset all tracking (useful for testing)
 */
export function resetFlightDataMonitor() {
  lastFlightUpdateTimestamp = null;
  flightsReceivedCount = 0;
  flightsDisplayedCount = 0;
  socketConnected = false;
  domDataRowCount = 0;
  lastHealthyRenderTimestamp = null;
  console.log('[FlightDataMonitor] Monitor reset');
}
